import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, toKSTInput, fromKSTInput, DROP_STATUSES, ITEM_TYPES, itemPhotoUrl } from "@/lib/api";

const MAX_ITEMS = 15;
const slug = (t) => t.toLowerCase().replace(/\s+/g, "-");
const blankItem = (type) => ({ key: Math.random().toString(36).slice(2), id: "", type, name: "", quantity: "", price: "" });

const PhotoPick = ({ it, sdId, onPick, idx }) => {
  const src = it.file ? URL.createObjectURL(it.file) : it.photo_version ? itemPhotoUrl(sdId, it) : null;
  return (
    <label className="h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-dashed border-zinc-600 grid place-items-center hover:border-violet-400 transition-colors" title="Item photo (optional)">
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-4 w-4 text-zinc-500" />}
      <input type="file" accept="image/*" className="hidden" data-testid={`special-item-photo-${idx}`} onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </label>
  );
};

const ItemRow = ({ it, onChange, onRemove, idx, sdId }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] sm:grid-cols-[minmax(0,1fr)_72px_84px_32px] gap-2 items-center" data-testid={`special-item-row-${idx}`}>
    <div className="col-span-3 sm:col-span-1 flex gap-2">
      <PhotoPick it={it} sdId={sdId} idx={idx} onPick={(file) => onChange({ file })} />
      <Input data-testid={`special-item-name-${idx}`} required maxLength={120} placeholder="Item name" value={it.name} onChange={(e) => onChange({ name: e.target.value })} className="bg-zinc-900" />
    </div>
    <Input data-testid={`special-item-qty-${idx}`} required type="number" min="1" step="1" placeholder="Qty" value={it.quantity} onChange={(e) => onChange({ quantity: e.target.value })} className="bg-zinc-900" />
    <Input data-testid={`special-item-price-${idx}`} required type="number" min="0" step="0.01" placeholder="Price" value={it.price} onChange={(e) => onChange({ price: e.target.value })} className="bg-zinc-900" />
    <button type="button" data-testid={`special-item-remove-${idx}`} onClick={onRemove} aria-label="Remove item" className="h-9 w-8 grid place-items-center rounded-lg text-zinc-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const TypeSection = ({ type, items, all, setItems, canAdd, sdId }) => (
  <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.04] p-3 space-y-2" data-testid={`special-type-section-${slug(type)}`}>
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold">{type}</span>
      <span className="text-[11px] text-zinc-500">Name · Qty · Price per item</span>
    </div>
    {items.map((it) => {
      const idx = all.indexOf(it);
      return <ItemRow key={it.key} it={it} idx={idx} sdId={sdId} onChange={(p) => setItems(all.map((x) => (x === it ? { ...x, ...p } : x)))} onRemove={() => setItems(all.filter((x) => x !== it))} />;
    })}
    <button type="button" disabled={!canAdd} data-testid={`special-add-item-${slug(type)}`} onClick={() => setItems([...all, blankItem(type)])}
      className="inline-flex items-center gap-1 text-xs font-semibold text-violet-300 hover:text-violet-100 disabled:opacity-40 transition-colors">
      <Plus className="h-3.5 w-3.5" /> Add {type} item
    </button>
  </div>
);

export default function SpecialDropFormDialog({ sd, onClose, onSaved }) {
  const editing = !!sd.id;
  const [f, setF] = useState({ name: sd.name || "", claims_close: toKSTInput(sd.claims_close), payment_due: toKSTInput(sd.payment_due), pay_to: sd.pay_to || "ThePastelFix", status: sd.status || "open" });
  const [types, setTypes] = useState(sd.item_types || []);
  const [items, setItems] = useState((sd.items || []).map((it) => ({ ...it, key: it.id })));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const toggleType = (t) => {
    if (types.includes(t)) {
      setTypes(types.filter((x) => x !== t));
      setItems(items.filter((i) => i.type !== t));
    } else {
      if (items.length >= MAX_ITEMS) return toast.error(`You already have ${MAX_ITEMS} items; remove one to add a new type`);
      setTypes([...types, t]);
      setItems([...items, blankItem(t)]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!types.length || !items.length) return toast.error("Tick an item type and add at least one item");
    const empty = types.find((t) => !items.some((i) => i.type === t));
    if (empty) return toast.error(`Add at least one ${empty} item or untick ${empty}`);
    const body = { ...f, claims_close: fromKSTInput(f.claims_close), payment_due: fromKSTInput(f.payment_due), item_types: types,
      items: items.map((i) => ({ id: i.id, type: i.type, name: i.name, quantity: Number(i.quantity), price: Number(i.price) })) };
    setBusy(true);
    try {
      const r = editing ? await api.put(`/special-drops/${sd.id}`, body) : await api.post("/special-drops", body);
      const uploads = items.map((it, i) => [it, r.data.items[i]]).filter(([it]) => it.file);
      const failed = (await Promise.allSettled(uploads.map(([it, saved]) => {
        const fd = new FormData();
        fd.append("file", it.file);
        return api.post(`/special-drops/${r.data.id}/items/${saved.id}/photo`, fd);
      }))).filter((x) => x.status === "rejected");
      if (failed.length) toast.error(errMsg(failed[0].reason, `Saved, but ${failed.length} photo(s) didn't upload`));
      else toast.success(editing ? "Special drop updated" : "Special drop created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="special-drop-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit special drop" : "New special drop"}</DialogTitle>
          <DialogDescription>Joiners claim items first come until each item's quantity runs out. Claims here aren't added to joiner balances.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Name</Label><Input data-testid="special-name-input" required maxLength={120} value={f.name} onChange={set("name")} className="bg-zinc-900" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Close Date (KST)</Label><Input data-testid="special-close-input" type="datetime-local" value={f.claims_close} onChange={set("claims_close")} className="bg-zinc-900" /></div>
            <div className="space-y-1.5"><Label>Payment Due by (KST)</Label><Input data-testid="special-payment-due-input" type="datetime-local" value={f.payment_due} onChange={set("payment_due")} className="bg-zinc-900" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Payment to</Label>
              <Select value={f.pay_to} onValueChange={set("pay_to")}>
                <SelectTrigger data-testid="special-pay-to-select" className="bg-zinc-900"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ThePastelFix">ThePastelFix</SelectItem><SelectItem value="ArekuTrades">ArekuTrades</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="special-status-select" className="bg-zinc-900"><SelectValue /></SelectTrigger>
                <SelectContent>{DROP_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Item Type</Label>
            <div className="flex flex-wrap gap-4">
              {ITEM_TYPES.map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={`special-type-${slug(t)}`} checked={types.includes(t)} onCheckedChange={() => toggleType(t)} /> {t}
                </label>
              ))}
            </div>
            <div className="text-xs text-zinc-500" data-testid="special-item-count">{items.length} of {MAX_ITEMS} items</div>
          </div>
          {ITEM_TYPES.filter((t) => types.includes(t)).map((t) => (
            <TypeSection key={t} type={t} sdId={sd.id} items={items.filter((i) => i.type === t)} all={items} setItems={setItems} canAdd={items.length < MAX_ITEMS} />
          ))}
          <Button type="submit" disabled={busy} data-testid="special-save-button" className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 text-white font-semibold">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {editing ? "Save changes" : "Create special drop"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
